import {FC, useState} from 'react';
import {Box, Grid, Typography, Paper, Button, Collapse, Alert} from '@mui/material';
import {formatKstDateTime} from '../../utils/dateTime';
import type {WordMagicCardInfo} from '../../types/wordbook';

interface Props {
    info: WordMagicCardInfo;
}

export const MagicCardMetaTab: FC<Props> = ({info}) => {
    const [showJson, setShowJson] = useState(false);

    const formattedJson = (() => {
        if (!info.cacheSummaryJson) return null;
        try {
            return JSON.stringify(JSON.parse(info.cacheSummaryJson), null, 2);
        } catch {
            return info.cacheSummaryJson;
        }
    })();

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Typography variant="subtitle2" gutterBottom>유저 상태</Typography>
                        {info.userStatus ? (
                            <Box display="flex" flexDirection="column" gap={1}>
                                <MetaRow label="상태" value={info.userStatus}/>
                                <MetaRow label="생성 시각"
                                         value={info.userGeneratedAt ? formatKstDateTime(info.userGeneratedAt) : '—'}/>
                                <MetaRow label="sourceVersion" value={info.userSourceVersion?.toString() ?? '—'}/>
                                <MetaRow label="deletedAt"
                                         value={info.userDeletedAt ? formatKstDateTime(info.userDeletedAt) : '—'}/>
                                <MetaRow label="createdAt"
                                         value={info.userCreatedAt ? formatKstDateTime(info.userCreatedAt) : '—'}/>
                                <MetaRow label="updatedAt"
                                         value={info.userUpdatedAt ? formatKstDateTime(info.userUpdatedAt) : '—'}/>
                            </Box>
                        ) : (
                            <Alert severity="info" variant="outlined" sx={{mt: 1}}>
                                유저가 이 단어의 매직카드를 요청한 적이 없습니다.
                            </Alert>
                        )}
                    </Paper>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Typography variant="subtitle2" gutterBottom>캐시 메타 (magic_card_cache)</Typography>
                        {info.cacheExists ? (
                            <Box display="flex" flexDirection="column" gap={1}>
                                <MetaRow label="provider" value={info.cacheProvider ?? '—'}/>
                                <MetaRow label="modelName" value={info.cacheModelName ?? '—'}/>
                                <MetaRow label="promptVersion" value={info.cachePromptVersion ?? '—'}/>
                                <MetaRow label="version" value={info.cacheVersion?.toString() ?? '—'}/>
                                <MetaRow label="hitCount" value={info.cacheHitCount?.toString() ?? '—'}/>
                                <MetaRow label="updatedAt"
                                         value={info.cacheUpdatedAt ? formatKstDateTime(info.cacheUpdatedAt) : '—'}/>
                            </Box>
                        ) : (
                            <Alert severity="info" variant="outlined" sx={{mt: 1}}>
                                전역 캐시가 없습니다.
                            </Alert>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            {formattedJson && (
                <Box>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowJson(v => !v)}
                    >
                        {showJson ? '원본 JSON 숨기기' : '원본 JSON 보기'}
                    </Button>
                    <Collapse in={showJson}>
                        <Paper
                            variant="outlined"
                            sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: 'grey.900',
                                color: 'grey.100',
                                overflowX: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                            }}
                        >
                            <pre style={{margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                                {formattedJson}
                            </pre>
                        </Paper>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

const MetaRow: FC<{label: string; value: string}> = ({label, value}) => (
    <Box display="flex" justifyContent="space-between" gap={1}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{textAlign: 'right', wordBreak: 'break-all'}}>{value}</Typography>
    </Box>
);
